import { humanizeVectorStoreError } from './collectionCheck';
import { containsSensitiveEndpointInfo, sanitizeErrorMessage } from './sanitizeErrorMessage';

const normalizeWhitespace = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const CONNECTION_ERROR_HINTS = [
  'httpconnectionpool',
  'newconnectionerror',
  'connection refused',
  'failed to establish a new connection',
  'max retries exceeded',
  'remotedisconnected',
  'read timed out',
  'connect timeout',
  'milvusexception',
  'fail connecting to server',
  'server unavailable',
  'illegal connection params',
];

const isTechnicalPipelineError = (value) => {
  const lower = normalizeWhitespace(value).toLowerCase();
  if (!lower) return false;
  return (
    CONNECTION_ERROR_HINTS.some((hint) => lower.includes(hint))
    || lower.includes('traceback (most recent call last)')
    || lower.includes('caused by ')
    || /^failed to call marker parsing service/i.test(lower)
    || /^marker parsing failed/i.test(lower)
    || /^httpconnectionpool/i.test(lower)
    || lower.includes('<milvusexception')
  );
};

const firstSentence = (value) => {
  const text = normalizeWhitespace(value);
  if (!text) return '';
  const match = text.match(/^(.+?[.!?])(?:\s|$)/);
  return match ? match[1].trim() : text;
};

const extractLastErrorSegment = (raw) => {
  const text = normalizeWhitespace(raw);
  if (!text) return '';
  const marker = 'last error:';
  const idx = text.toLowerCase().lastIndexOf(marker);
  return idx >= 0 ? normalizeWhitespace(text.slice(idx + marker.length)) : text;
};

const MARKER_PARSING_ISSUE_MESSAGE = 'File Parsing Issue. Retry/Reupload the file';

const isMarkerParsingIssue = (value, { processingStage } = {}) => {
  const lower = normalizeWhitespace(value).toLowerCase();
  const stage = String(processingStage || '').toLowerCase();
  if (!lower && !stage) return false;
  return (
    stage.includes('marker')
    || lower.includes('marker conversion failed')
    || lower.includes('marker parsing failed')
    || lower.includes('failed to call marker parsing service')
    || lower.includes('/parse/submit')
    || lower.includes('/marker_single')
    || lower.includes('marker_single')
  );
};

const isMilvusPipelineError = (lower) => (
  lower.includes('milvus')
  || lower.includes('pymilvus')
  || lower.includes('milvusexception')
  || lower.includes('vector store')
  || lower.includes('fail connecting to server')
  || lower.includes('server unavailable')
  || lower.includes('illegal connection params')
);

const humanizePipelineErrorText = (raw, { processingStage, processingDetail } = {}) => {
  const detail = normalizeWhitespace(processingDetail);
  const stage = String(processingStage || '').toLowerCase();
  const source = extractLastErrorSegment(raw) || normalizeWhitespace(raw);
  if (!source) {
    if (stage === 'guardrail_blocked' && detail) {
      return detail;
    }
    return '';
  }

  const lower = source.toLowerCase();

  if (isMarkerParsingIssue(source, { processingStage: stage })) {
    return MARKER_PARSING_ISSUE_MESSAGE;
  }

  if (lower.includes('missing doc_id') || lower.includes('missing doc_id/file_key')) {
    return 'This file record is missing its document identifier, so the pipeline cannot process it.';
  }

  if (lower.includes('pre-embedding guardrails blocked') || stage === 'guardrail_blocked') {
    const violations = source.replace(/^pre-embedding guardrails blocked:\s*/i, '').trim();
    if (violations) {
      return `Content was blocked by pre-embedding guardrails: ${violations}`;
    }
    return detail || 'Content was blocked by pre-embedding guardrails.';
  }

  if (lower.includes('marker async parse timed out') || lower.includes('parse timed out')) {
    const timeoutMatch = source.match(/timed out after (\d+)\s*seconds?/i);
    if (timeoutMatch) {
      return `PDF parsing exceeded the ${timeoutMatch[1]}-second limit. Try a smaller file or retry when the parser service is less busy.`;
    }
    return 'PDF parsing exceeded the allowed time limit. Try a smaller file or retry later.';
  }

  if (
    CONNECTION_ERROR_HINTS.some((hint) => lower.includes(hint))
    || lower.includes('/parse/submit')
    || lower.includes('failed to call marker parsing service')
    || lower.includes('marker parsing failed')
    || lower.includes('marker parse')
  ) {
    if (isMilvusPipelineError(lower)) {
      return humanizeVectorStoreError(source, 'milvus');
    }
    if (
      CONNECTION_ERROR_HINTS.some((hint) => lower.includes(hint))
      || lower.includes('/parse/submit')
    ) {
      return 'The PDF parser service could not be reached. Verify that the parser is running and reachable from the application.';
    }
    return 'PDF parsing failed before the document could be converted to text.';
  }

  if (lower.includes('markdown not found')) {
    return 'Parsed markdown was not found for this document, so later pipeline steps could not run.';
  }

  if (lower.includes('maximum context length') || lower.includes('invalid_request_error') || (lower.includes('requested') && lower.includes('tokens'))) {
    return 'A model rejected the request because the content exceeded the allowed token limit.';
  }

  if (lower.includes('graphrag') || lower.includes('neo4j')) {
    return 'Graph ingestion failed while sending this document to the knowledge graph.';
  }

  if (isMilvusPipelineError(lower)) {
    return humanizeVectorStoreError(source, 'milvus');
  }

  if (lower.includes('embedding') || lower.includes('embeddings')) {
    return 'Embedding generation failed while preparing this file for search.';
  }

  if (lower.includes('s3') || lower.includes('nosuchkey') || lower.includes('accessdenied') || lower.includes('knowledge source')) {
    return 'Knowledge Source access failed while reading or writing this file.';
  }

  if (lower.includes('strategy comparison failed') || lower.includes('strategy fallback failed') || lower.includes('choosing_strategy')) {
    return 'Chunking strategy selection failed for this document.';
  }

  if (lower.includes('delete event') || lower.includes('cancelled by delete')) {
    return 'Processing stopped because the file was deleted while the pipeline was running.';
  }

  if (stage === 'failed' && detail) {
    return firstSentence(detail);
  }

  return firstSentence(source);
};

export const formatPipelineError = (record = {}) => {
  const status = String(record?.processing_status || '').toLowerCase();
  const raw = normalizeWhitespace(record?.last_error || record?.error);
  const detail = normalizeWhitespace(record?.processing_detail);
  const storedMessage = normalizeWhitespace(record?.last_error_message);
  const context = {
    processingStage: record?.processing_stage,
    processingDetail: detail,
  };

  if (isMarkerParsingIssue(raw || detail, context)) {
    return MARKER_PARSING_ISSUE_MESSAGE;
  }

  if (storedMessage && !isTechnicalPipelineError(storedMessage)) {
    const cleanedStored = sanitizeErrorMessage(storedMessage);
    if (cleanedStored) return cleanedStored;
  }

  if (raw) {
    const humanized = sanitizeErrorMessage(humanizePipelineErrorText(raw, context));
    if (humanized && !isTechnicalPipelineError(humanized)) return humanized;
  }

  if (detail) {
    const humanizedDetail = sanitizeErrorMessage(humanizePipelineErrorText(detail, context));
    if (humanizedDetail && !isTechnicalPipelineError(humanizedDetail)) return humanizedDetail;
  }

  if (!raw && status !== 'failed') return '';
  if (!raw) {
    return status === 'failed'
      ? 'This file failed during processing. Retry the file or check the application logs.'
      : '';
  }

  return sanitizeErrorMessage(
    humanizePipelineErrorText(raw, context),
    { fallback: 'This file failed during processing. Retry the file or check the application logs.' }
  );
};

export const getPipelineErrorReference = (record = {}) => {
  const raw = normalizeWhitespace(record?.last_error || record?.error);
  const detail = normalizeWhitespace(record?.processing_detail);
  if (isMarkerParsingIssue(raw || detail, { processingStage: record?.processing_stage })) {
    return '';
  }
  if (containsSensitiveEndpointInfo(raw) || containsSensitiveEndpointInfo(detail)) {
    return '';
  }
  const summary = formatPipelineError(record);
  const reference = raw || detail || '';
  if (!reference || reference === summary || isTechnicalPipelineError(reference)) {
    return '';
  }
  return reference;
};
